import { useTranslation } from "react-i18next";
import { Avatar, IconButton, Tooltip } from "@bdo/cornerstone-ui";
import {
    RiArrowGoBackLine,
    RiDeleteBinLine,
    RiNotification3Line,
    RiNotificationOffLine,
} from "@remixicon/react";
import type { AccessControlEntry, ValidUser } from "@/app/workflows/workflowInstancesApi";

interface AccessControlListProps {
    entries: AccessControlEntry[];
    currentUserEmail: string | undefined;
    usersToDelete: string[];
    notificationOverrides: Record<string, boolean>;
    temporaryAddedUsers: ValidUser[];
    onToggleNotifications: (entry: AccessControlEntry) => void;
    onDeleteUser: (entry: AccessControlEntry) => void;
    onRemoveTemporaryUser: (email: string) => void;
}

export function AccessControlList({
    entries,
    currentUserEmail,
    usersToDelete,
    notificationOverrides,
    temporaryAddedUsers,
    onToggleNotifications,
    onDeleteUser,
    onRemoveTemporaryUser,
}: Readonly<AccessControlListProps>) {
    const { t } = useTranslation();

    return (
            <div className="flex flex-col gap-2xs mt-xs" data-testid="access-control-list">
                {entries.map((entry) => {
                    const isMarkedForDeletion = usersToDelete.includes(entry.email);
                    const isNotificationEnabled = notificationOverrides[entry.email] ?? entry.isNotificationEnabled;
                    return (
                        <div
                            key={entry.email}
                            data-testid={`access-control-entry-${entry.email}`}
                            className={`flex items-center justify-between px-xs py-sm ${isMarkedForDeletion ? "bg-error-lightest rounded-sm" : ""}`}
                        >
                            <div className="flex items-center gap-xs flex-1 min-w-0">
                                <div className="relative flex-shrink-0" title={entry.name || entry.email}>
                                    <Avatar size="md" name={entry.name || entry.email} isCircle />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="text-base text-mono-darker truncate" title={entry.name || entry.email}>
                                        {entry.name || entry.email}
                                    </div>
                                    <div className="flex items-center gap-2xs text-sm text-mono">
                                        <span className="truncate" title={entry.email}>{entry.email}</span>
                                        {entry.isOwner && (
                                            <>
                                                <span className="w-2xs h-2xs bg-mono rounded-full flex-shrink-0" />
                                                <span className="flex-shrink-0">{t("manageAccess.owner", { defaultValue: "Owner" })}</span>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="flex items-center justify-center flex-shrink-0">
                                {currentUserEmail?.toLowerCase() === entry.email.toLowerCase() && (
                                    <Tooltip
                                        description={
                                            isNotificationEnabled
                                                ? t("manageAccess.disableNotifications", { defaultValue: "Turn off MS Teams notifications" })
                                                : t("manageAccess.enableNotifications", { defaultValue: "Turn on MS Teams notifications" })
                                        }
                                        className={"w-[264px] mr-xs"}
                                    >
                                        <IconButton
                                            icon={
                                                isNotificationEnabled ? (
                                                    <RiNotification3Line className="w-xl h-xl" />
                                                ) : (
                                                    <RiNotificationOffLine className="w-xl h-xl" />
                                                )
                                            }
                                            onClick={() => onToggleNotifications(entry)}
                                            aria-label={
                                                isNotificationEnabled
                                                    ? t("manageAccess.disableNotifications", { defaultValue: "Turn off MS Teams notifications" })
                                                    : t("manageAccess.enableNotifications", { defaultValue: "Turn on MS Teams notifications" })
                                            }
                                            size="sm"
                                            className="text-mono-darker"
                                            data-testid={`toggle-notifications-${entry.email}`}
                                        />
                                    </Tooltip>
                                )}

                                {!entry.isOwner && (
                                    <Tooltip
                                        description={
                                            isMarkedForDeletion
                                                ? t("manageAccess.undoRemoveUser", {defaultValue: "Undo remove"})
                                                : t("manageAccess.removeUser", { defaultValue: "Remove user",})
                                        }
                                        className={"w-[150px] mr-xs"}
                                    >
                                        <IconButton
                                            icon={
                                                isMarkedForDeletion
                                                    ? <RiArrowGoBackLine className="w-xl h-xl" />
                                                    : <RiDeleteBinLine className="w-xl h-xl" />
                                            }
                                            onClick={() => onDeleteUser(entry)}
                                            aria-label={
                                                isMarkedForDeletion
                                                    ? t("manageAccess.undoRemoveUser", { defaultValue: "Undo remove" })
                                                    : t("manageAccess.removeUser", { defaultValue: "Remove user" })
                                            }
                                            size="sm"
                                            className={isMarkedForDeletion ? "text-mono-darker" : "text-error"}
                                            data-testid={`delete-user-${entry.email}`}
                                        />
                                    </Tooltip>
                                )}
                            </div>
                        </div>
                    );
                })}

                {temporaryAddedUsers.map((user) => (
                    <div
                        key={`temp-${user.email}`}
                        data-testid={`temporary-user-${user.email}`}
                        className="flex items-center justify-between px-xs py-sm bg-mono-lightest rounded-sm"
                    >
                        <div className="flex items-center gap-xs flex-1 min-w-0">
                            <div className="relative flex-shrink-0" title={user.displayName || user.name || user.email}>
                                <Avatar size="md" name={user.displayName || user.email} isCircle />
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="text-base text-mono-darker truncate" title={user.displayName || user.name || user.email}>
                                    {user.displayName || user.name || user.email}
                                </div>
                                <div className="text-sm text-mono truncate" title={user.email}>
                                    {user.email}
                                </div>
                            </div>
                        </div>

                        <div className="flex items-center justify-center flex-shrink-0">
                            <Tooltip
                                description={t("manageAccess.removeUser", { defaultValue: "Remove user" })}
                                className={"w-[150px] mr-xs"}
                            >
                                <IconButton
                                    icon={<RiDeleteBinLine className="w-xl h-xl" />}
                                    onClick={() => onRemoveTemporaryUser(user.email)}
                                    aria-label={t("manageAccess.removeUser", { defaultValue: "Remove user" })}
                                    size="sm"
                                    className="text-error"
                                    data-testid={`remove-temporary-user-${user.email}`}
                                />
                            </Tooltip>
                        </div>
                    </div>
                ))}
            </div>
    );
}